// @flow
import type { Effect, Event } from 'effector'
import type { FormRequestFail, Fail, Done, SocialProvider } from 'core/types'

import { UserService } from 'services/user'
import type {
  ChangePasswordParams,
  ChangePasswordResponse,
  RestoreResponse,
  RestoreRequest,
  ResetRequest,
  ResetResponse,
  RestoreByPhoneRequest,
  RestoreByPhoneResponse,
  SignUpParams,
  SignUpWithPhoneParams,
  AutoSignUpParams,
  AutoSignUpResponse,
  SignUpResponse,
  LoginParams,
} from '../types'
import * as api from '../api'
import { domain } from './domain'

export const initializeSession: Event<void> = domain.createEvent('initialize user client session')

export const logInFx: Effect<LoginParams, void> = domain.createEffect('logout', {
  async handler(params) {
    await api.logIn(params)

    return undefined
  },
})

export const logOutFx: Effect<void, void, void> = domain.createEffect('logout', {
  async handler() {
    await api.logOut()

    return undefined
  },
})

export const deleteAccountFx: Effect<void, void, Fail> = domain.createEffect("delete user's account", {
  async handler() {
    await api.deleteAccount()
  },
})

export const signUpFx: Effect<SignUpParams, SignUpResponse, Fail> = domain.createEffect('sign up user', {
  async handler({ provider, user }) {
    const res = await api.signUp({ user, provider })

    await UserService.getUser('me')

    return res
  },
})

export const autoSignUpFx: Effect<AutoSignUpParams, Done | boolean, Fail> = domain.createEffect('auto sign up user', {
  async handler({ couponCampaignIds, user }: AutoSignUpParams) {
    await api.autoSignUp(user, couponCampaignIds)
    await UserService.getUser('me')

    return true
  },
})

export const signUpWithPhoneOnlyFx: Effect<SignUpWithPhoneParams, AutoSignUpResponse, Fail> = domain.createEffect(
  'sign up user with phone only',
  {
    async handler(params) {
      const res = await api.signUpWithPhoneOnly(params)

      await UserService.getUser('me')

      return res
    },
  }
)

export const changePasswordFx: Effect<ChangePasswordParams, ChangePasswordResponse, Fail> = domain.createEffect(
  'change password',
  {
    async handler(values) {
      const response = await api.changePassword(values)

      return response
    },
  }
)

export const restorePasswordFx: Effect<RestoreRequest, RestoreResponse, FormRequestFail> = domain.createEffect(
  'restore password',
  {
    async handler(values) {
      const response = await api.restorePassword(values)

      return response
    },
  }
)

export const restorePasswordByPhoneFx: Effect<
  RestoreByPhoneRequest,
  RestoreByPhoneResponse,
  FormRequestFail
> = domain.createEffect('restore password by phone', {
  async handler(values) {
    const response = await api.restorePasswordByPhone(values)

    return response
  },
})

export const resetPasswordFx: Effect<ResetRequest, ResetResponse, FormRequestFail> = domain.createEffect(
  'reset password',
  {
    async handler(values) {
      const response = await api.resetPassword(values)

      return response
    },
  }
)

export const unlinkSocial: Event<SocialProvider> = domain.createEvent("get user's documents")

export const unlinkSocialFx: Effect<SocialProvider, void, Fail> = domain.createEffect('unlink social net', {
  async handler(provider) {
    await api.unlinkSocial(provider)

    return undefined
  },
})
